import { useMemo, useState } from 'react';

import { PageHeader } from '@/components/app-shell/PageHeader';

import { useTenants, useUserActivity } from './useTenants';
import type { UserActivity } from './admin-api';

/**
 * Platform Admin User Activity page (SCRUM-575): a cross-tenant table of users
 * with display name, email, role, status, created date, and last login —
 * filterable by tenant and sortable. Backed by GET /api/admin/users. Matches the
 * v7 admin console style (bg-bg-4 card, mono column headers, status pills).
 */

type SortKey = 'lastLoginAt' | 'createdAt' | 'displayName';

const STATUS_PILL: Record<string, string> = {
  Active: 'text-[var(--success)] bg-[var(--success-dim)]',
  Pending: 'text-[var(--warning)] bg-[var(--warning-dim)]',
  Deactivated: 'text-[var(--danger)] bg-[var(--danger-dim)]',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function UserActivityPage() {
  const { tenants } = useTenants();
  const [tenantFilter, setTenantFilter] = useState<string>('');
  const [sortKey, setSortKey] = useState<SortKey>('lastLoginAt');
  const { users, isLoading, isError } = useUserActivity(tenantFilter || undefined);

  const sorted = useMemo(() => {
    const rows = [...users];
    rows.sort((a, b) => {
      if (sortKey === 'displayName') {
        return a.displayName.localeCompare(b.displayName);
      }
      // Date desc, nulls last.
      const av = a[sortKey];
      const bv = b[sortKey];
      if (av === null && bv === null) return 0;
      if (av === null) return 1;
      if (bv === null) return -1;
      return bv.localeCompare(av);
    });
    return rows;
  }, [users, sortKey]);

  return (
    <div className="flex min-h-full flex-col" data-testid="admin-user-activity-page">
      <PageHeader
        title="User Activity"
        subtitle={`${String(users.length)} users · who's using the platform and when`}
        actions={
          <div className="flex items-center gap-2">
            <span className="text-text-3 font-mono text-[0.6875rem] tracking-[0.03em] uppercase">
              Tenant
            </span>
            <select
              aria-label="Filter by tenant"
              value={tenantFilter}
              onChange={(e) => {
                setTenantFilter(e.target.value);
              }}
              className="bg-bg-1 border-border-0 rounded-tk-sm text-text-1 border px-2.5 py-1.5 text-sm"
            >
              <option value="">All tenants</option>
              {tenants.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.bankName}
                </option>
              ))}
            </select>
            <select
              aria-label="Sort by"
              value={sortKey}
              onChange={(e) => {
                setSortKey(e.target.value as SortKey);
              }}
              className="bg-bg-1 border-border-0 rounded-tk-sm text-text-1 border px-2.5 py-1.5 text-sm"
            >
              <option value="lastLoginAt">Last login</option>
              <option value="createdAt">Created</option>
              <option value="displayName">Name</option>
            </select>
          </div>
        }
      />

      <div className="p-4">
        <div className="bg-bg-4 rounded-tk-lg border-border-0 overflow-x-auto border">
          {isError ? (
            <p className="text-text-3 p-6 text-sm">Couldn&apos;t load user activity. Try again.</p>
          ) : isLoading ? (
            <p className="text-text-3 p-6 text-sm">Loading…</p>
          ) : sorted.length === 0 ? (
            <p className="text-text-3 p-6 text-sm">No users to show.</p>
          ) : (
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="text-text-3 border-border-0 border-b text-left font-mono text-[0.6875rem] tracking-[0.03em] uppercase">
                  <th className="px-4 py-2.5 font-semibold">Name</th>
                  <th className="px-4 py-2.5 font-semibold">Email</th>
                  <th className="px-4 py-2.5 font-semibold">Tenant</th>
                  <th className="px-4 py-2.5 font-semibold">Role</th>
                  <th className="px-4 py-2.5 font-semibold">Status</th>
                  <th className="px-4 py-2.5 font-semibold">Created</th>
                  <th className="px-4 py-2.5 font-semibold">Last login</th>
                </tr>
              </thead>
              <tbody>
                {sorted.map((u: UserActivity) => (
                  <tr key={u.id} className="border-border-0 hover:bg-bg-2 border-b last:border-0">
                    <td className="text-text-0 px-4 py-2.5">{u.displayName}</td>
                    <td className="text-text-2 px-4 py-2.5 font-mono text-xs">{u.email}</td>
                    <td className="text-text-2 px-4 py-2.5">{u.tenantName}</td>
                    <td className="text-text-2 px-4 py-2.5">{u.role}</td>
                    <td className="px-4 py-2.5">
                      <span
                        className={`rounded-tk-sm px-2 py-0.5 text-xs font-medium ${
                          STATUS_PILL[u.status] ?? 'text-text-3 bg-bg-2'
                        }`}
                      >
                        {u.status}
                      </span>
                    </td>
                    <td className="text-text-2 px-4 py-2.5">{formatDate(u.createdAt)}</td>
                    <td className="text-text-2 px-4 py-2.5">{formatDate(u.lastLoginAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
